import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Mail, MapPin, Phone, FileText, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

// Add a type for the privacy section items
type PrivacyItem = string;

export const Footer = () => {
  const { t } = useTranslation();
  const [showPrivacy, setShowPrivacy] = useState(false);

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-50 dark:bg-gray-800 border-t border-slate-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100 mb-4">
              {t('footer.contact.title')}
            </h3>
            <div className="space-y-3 text-slate-600 dark:text-gray-300">
              <a href="mailto:victor@victorlazari.com" className="flex items-center gap-2 hover:text-slate-900 dark:hover:text-gray-100 transition-colors">
                <Mail className="w-4 h-4" />
                {t('footer.contact.email')}
              </a>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {t('footer.contact.phone')}
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {t('footer.contact.address')}
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('footer.contact.piva')}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100 mb-4">
              {t('footer.quickLinks.title')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100 transition-colors">
                  {t('navigation.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-600 hover:text-slate-900 transition-colors">
                  {t('navigation.about')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-600 hover:text-slate-900 transition-colors">
                  {t('navigation.blog')}
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className="text-slate-600 hover:text-slate-900 transition-colors">
                  {t('navigation.testimonials')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">
                  {t('navigation.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100 mb-4">
              {t('footer.services.title')}
            </h3>
            <ul className="space-y-2">
              <li className="text-slate-600 dark:text-gray-300">{t('footer.services.ai')}</li>
              <li className="text-slate-600 dark:text-gray-300">{t('footer.services.automation')}</li>
              <li className="text-slate-600 dark:text-gray-300">{t('footer.services.development')}</li>
              <li className="text-slate-600 dark:text-gray-300">{t('footer.services.consulting')}</li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100 mb-4">
              {t('footer.legal.title')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/privacy"
                  className="text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100 transition-colors flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  {t('navigation.legal.privacy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100 transition-colors flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  {t('navigation.legal.terms')}
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100 transition-colors flex items-center gap-2"
                >
                  <Cookie className="w-4 h-4" />
                  {t('navigation.legal.cookies')}
                </Link>
              </li>
              <li>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('openCookieConsent'))}
                  className="text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100 transition-colors flex items-center gap-2 w-full text-left"
                >
                  <Settings className="w-4 h-4" />
                  {t('cookies.cookieConsent.buttons.customize')}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 dark:border-gray-700 text-center text-slate-600 dark:text-gray-300">
          <p>{t('footer.copyright', {year: currentYear})}</p>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <AnimatePresence>
        {showPrivacy && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
              onClick={() => setShowPrivacy(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.9 }}
              className="fixed inset-4 md:inset-10 bg-white dark:bg-gray-800 rounded-lg z-50 overflow-hidden flex flex-col transition-colors"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-semibold text-cyan-600 dark:text-cyan-400 flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  {t('privacy.title')}
                </h2>
                <button
                  onClick={() => setShowPrivacy(false)}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 p-6">
                <div className="max-w-4xl mx-auto prose prose-slate dark:prose-invert prose-cyan">
                  <h3>{t('privacy.section1.title')}</h3>
                  <p>{t('privacy.section1.content')}</p>
                  <ul>
                    {(t('privacy.section1.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section2.title')}</h3>
                  <p>{t('privacy.section2.content')}</p>
                  <ul>
                    {(t('privacy.section2.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section3.title')}</h3>
                  <p>{t('privacy.section3.content')}</p>
                  <ul>
                    {(t('privacy.section3.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section4.title')}</h3>
                  <p>{t('privacy.section4.content')}</p>
                  <ul>
                    {(t('privacy.section4.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section5.title')}</h3>
                  <p>{t('privacy.section5.content')}</p>
                  <ul>
                    {(t('privacy.section5.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section6.title')}</h3>
                  <p>{t('privacy.section6.content')}</p>
                  <ul>
                    {(t('privacy.section6.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section7.title')}</h3>
                  <p>{t('privacy.section7.content')}</p>
                  <ul>
                    {(t('privacy.section7.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section8.title')}</h3>
                  <p>{t('privacy.section8.content')}</p>
                  <ul>
                    {(t('privacy.section8.items', { returnObjects: true }) as PrivacyItem[]).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3>{t('privacy.section9.title')}</h3>
                  <p>{t('privacy.section9.content')}</p>
                  <div className="bg-gray-800/50 p-4 rounded-lg mt-4">
                    <p className="font-semibold mb-2">{t('privacy.section9.contact.title')}</p>
                    <p>{t('privacy.section9.contact.email')}</p>
                    <p>{t('privacy.section9.contact.address')}</p>
                  </div>

                  <h3>{t('privacy.section10.title')}</h3>
                  <p>{t('privacy.section10.content')}</p>
                  <p className="text-sm text-gray-400 mt-4">
                    {t('privacy.lastUpdated')}: {t('privacy.lastUpdatedDate')}
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </footer>
  );
};